import { useCallback } from "react";
import {
  confirmCategorization,
  correctCategorization,
  notATransfer,
  refundCategorization,
} from "../api/transactions";
import type { Categorization } from "../types/transaction";
import { CLEARING_ACCOUNT } from "../utils/accounts";
import type { CardExit } from "./useCardExit";

/** How a refused answer is reported: the error and the row. */
export type ReportRefusal = (err: unknown, itemId?: number) => void;

export interface DeckAnswers {
  confirm: (itemId: number) => Promise<void>;
  correct: (itemId: number, account: string) => Promise<void>;
  /** "Transfer between my accounts": confirm a fenced row's clearing suggestion, else correct. */
  transfer: (item: Categorization) => Promise<void>;
  notATransfer: (itemId: number, otherTransactionId: number) => Promise<void>;
  refund: (itemId: number) => Promise<void>;
}

/**
 * The answers a deck card sends. Confirm and correct take the card off the
 * deck (useCardExit); `not_a_transfer` and `refund` answer in place, and the
 * list is fetched again, which refreshes both rows of the pair the answer's
 * `rescored` names. Every refusal goes to `report` with the row's id.
 */
export function useDeckAnswers(
  clientId: number,
  exit: CardExit,
  onUpdated: () => void,
  report: ReportRefusal,
): DeckAnswers {
  const { runExit } = exit;
  const confirm = useCallback(
    (id: number) =>
      runExit(
        id,
        () => confirmCategorization(clientId, id),
        (err) => report(err, id),
      ),
    [clientId, runExit, report],
  );
  const correct = useCallback(
    (id: number, account: string) =>
      runExit(
        id,
        () => correctCategorization(clientId, id, account),
        (err) => report(err, id),
      ),
    [clientId, runExit, report],
  );
  const inPlace = useCallback(
    async (id: number, send: () => Promise<unknown>) => {
      try {
        await send();
        onUpdated();
      } catch (err) {
        report(err, id);
      }
    },
    [onUpdated, report],
  );
  return {
    confirm,
    correct,
    transfer: (item) =>
      item.account_name === CLEARING_ACCOUNT
        ? confirm(item.id)
        : correct(item.id, CLEARING_ACCOUNT),
    notATransfer: (id, other) => inPlace(id, () => notATransfer(clientId, id, other)),
    refund: (id) => inPlace(id, () => refundCategorization(clientId, id)),
  };
}
